import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { categoryToUrl, isTokenExpired, navigateToLogin } from '../utils';

const CategoryCardContainer = styled.div`
  display: flex;
  text-decoration: none;
  color: inherit;
  width: 12vw;
  border-radius: 5px;
  padding: 20px 30px;
  text-align: center;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: box-shadow 0.3s ease-in-out;
  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  }

  /* Tablet responsive */
  @media (max-width: 768px) {
    width: 100%;
    padding: 15px 10px;
    box-sizing: border-box;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    width: calc(50% - 4px);
    min-width: calc(50% - 4px);
    max-width: calc(50% - 4px);
    padding: 10px 2px;
    box-sizing: border-box;
    flex-shrink: 0;
  }

  /* Small mobile responsive */
  @media (max-width: 400px) {
    width: calc(50% - 4px);
    min-width: calc(50% - 4px);
    max-width: calc(50% - 4px);
    padding: 8px 3px;
    box-sizing: border-box;
  }

  /* Touch device fallback */
  @media (hover: none) and (pointer: coarse) {
    &:active {
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    }
  }
`;

const CategoryImage = styled.img`
  width: 12vw;
  height: 12vw;
  object-fit: cover;
  margin: 0 auto;

  /* Tablet responsive */
  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    width: 90px;
    height: 90px;
    display: block;
    margin: 0 auto;
  }

  /* Small mobile responsive */
  @media (max-width: 400px) {
    width: 80px;
    height: 80px;
  }
`;

const CategoryName = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  margin: 10px 0 5px;
  height: 2.6em;
  line-height: 1.3em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  width: 100%;

  /* Tablet responsive */
  @media (max-width: 768px) {
    font-size: 0.9rem;
    height: 2.4em;
    line-height: 1.2em;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    font-size: 0.6rem;
    line-height: 1.3em;
    text-align: center;
    margin: 6px auto 4px;
    height: auto;
    min-height: 1.3em;
    display: block;
    overflow: visible;
    white-space: nowrap;
    width: 100%;
    padding: 0 2px;
    box-sizing: border-box;
  }

  /* Small mobile responsive */
  @media (max-width: 400px) {
    font-size: 0.55rem;
    line-height: 1.2em;
    min-height: 1.2em;
  }
`;

export default function CategoryCard({ data, style }) {
  const navigate = useNavigate();

  const { name, image } = data;

  const handleClick = () => {
    const token = localStorage.getItem('token');

    // Check if user is logged in
    const categorySlug = categoryToUrl(name);
    const categoryPath = `/search?category=${encodeURIComponent(categorySlug)}`;

    if (!token || isTokenExpired(token)) {
      // Redirect to login page if not logged in
      navigateToLogin(navigate, categoryPath);
    } else {
      // Navigate to category search if logged in
      navigate(categoryPath);
    }
  };

  return (
    <CategoryCardContainer onClick={handleClick} style={style}>
      <CategoryImage src={image} alt={name} />
      <CategoryName>{name}</CategoryName>
    </CategoryCardContainer>
  );
}
